// Package Imports
import regex from 'core/constants/Regex';
import disabledDate from 'core/utils/disabledDate';
import disabledTime from 'core/utils/disabledTime';
import { useWatch } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { Checkbox, Col, Form, Row } from 'antd';

// Project Imports
import TextInput from 'components/text/textInput';
import DropdownInput from 'components/dropdown/dropDownInput';
import constants from 'core/constants/Constants';
import RangeInput from 'components/date/rangeInput';
import SegmentInput from 'components/segment/SegmentInput';
import { RuleDescription } from 'pages/rules/subComponents';
import { useRulesCreateState } from 'pages/rules/context/RulesCreateStateContext';
// prettier-ignore
import { activeStatus, ConfigurationStatus, clawbackTypeOptions } from 'pages/rules/fileTypesMapping';
// eslint-disable-next-line import/extensions
import useRulesDropdown from './RulesDropdown';

// prettier-ignore
const ruleDetailsItem = ({ form, type, disabled, t, ruleType, ruleGroupDate, tagsChecked, }: any) => {
  const state = useRulesCreateState();
  const { adjustmentLevel } = useRulesDropdown();
  // prettier-ignore
  const isLatest = (state.previousValues as any)?.is_latest;
  const ruleVersionList = useWatch('rule_version_list', { form, preserve: true, });
  const newruleVersion = useWatch('rule_version', { form, preserve: true, });
  const adjustmentLevelVal = useWatch('adjustment_level', { form, preserve: true, });
  const ruleGroup = useWatch('rule_group', { form, preserve: true, });
  const newRuleData = state.ruleData?.find(
    (item) => item?.rule_version === newruleVersion
  );
  const ruleStartDate = dayjs(newRuleData?.rule_start_date);
  const ruleEndndDate = dayjs(newRuleData?.rule_end_date);
  const currentRuleDate = [ruleStartDate, ruleEndndDate];

  // Helper to update ruleData array in state
  const updateRuleData = (updatedFields: any) => {
    if (!state.ruleData) return;
    const updatedRuleData = state.ruleData.map((item: any) => {
      if (item?.rule_version === newruleVersion) {
        return { ...item, ...updatedFields };
      }
      return item;
    });
    state.setRuleData(updatedRuleData);
  };

  // Handler for rule_group_configuration_status change
  const handleConfigStatusChange = (value: any) => {
    // If set to true, set file_type to 'dmn'
    if (value === true || value === 'true') {
      updateRuleData({
        rule_group_configuration_status: true,
        rule_file_type: 'dmn',
      });
      form.setFieldsValue({
        rule_group_configuration_status: true,
        rule_file_type: 'dmn',
      });
    } else {
      updateRuleData({ rule_group_configuration_status: false });
      form.setFieldsValue({
        rule_group_configuration_status: false,
      });
    }
  };
  const handleAdjustmentLevelChange = (value: any) => {
    if (value === 'transaction') {
      updateRuleData({
        adjustment_level: value,
        rule_file_type: 'dmn',
        rule_group_configuration_status: true,
        rule_applicability_mode_type: 'single',
      });
      form.setFieldsValue({
        adjustment_level: value,
        rule_file_type: 'dmn',
        rule_group_configuration_status: true,
        rule_applicability_mode_type: 'single',
      });
    } else {
      updateRuleData({ adjustment_level: value });
      form.setFieldsValue({
        adjustment_level: value,
      });
    }
  };

  function getRuleGroupList() {
    if (type?.toLowerCase() === 'edit') {
      const currentType = state?.ruleGroupList?.filter(
        (item) => item?.unique_id === ruleGroup
      )?.[0]?.rule_type;
      const filteredRuleGroupList = state?.ruleGroupList?.filter(
        (item) => item?.rule_type === currentType
      );
      return filteredRuleGroupList?.map((item: any) => ({
        label: `${item?.rule_group_name} `,
        value: item?.unique_id,
      }));
    }
    return state.ruleGroupList?.map((item: any) => ({
      label: `${item?.rule_group_name} `,
      value: item?.unique_id,
    }));
  }

  return (
    <div className="">
      <Row gutter={16}>
        <Col order={1} xs={24} sm={24} lg={8}>

          <TextInput
            label={t('rules.ruleName')}
            name="rule_name"
            required={true}
            message={t('rules.messages.ruleName')}
            placeholder={t('rules.messages.ruleName')}
            disabled={type === 'Show' || (state.previousValues && !isLatest)}
            rules={{
              pattern: regex.ruleNameCheck,
              message: t('errorMessages.rulenameValidMsg'),
            }}
          />
        </Col>
        <Col order={8} xs={24} sm={24} lg={8}>
          <Form.Item name="merge_with_tag_name" valuePropName="checked" className="mb-0">
            <Checkbox disabled={type === 'Show' || disabled}>
              {t('rules.tagRule')}
            </Checkbox>
          </Form.Item>
        </Col>
        {tagsChecked ? (
          <Col order={9} xs={24} sm={24} lg={8}>
            <TextInput
              label={t('rules.tagName')}
              name="tags"
              required={true}
              message={t('rules.messages.tagName')}
              placeholder={t('rules.tagName')}
              disabled={type === 'Show' || disabled}
            />
          </Col>
        ) : null}
        <Col
          order={type?.toLowerCase() === 'show' ? 2 : 4}
          xs={24}
          md={18}
          lg={8}
        >
          <RangeInput
            label={t('rules.ruleDate')}
            name="rules_date"
            required={true}
            message={t('rules.messages.ruleDate')}
            format={constants.yyyymmddhhmmss} // Changed to include date and time format
            disabled={
              form.getFieldValue('rules_date')?.length < 2 ||
              form.getFieldValue('rule_group') === undefined ||
              type === 'Show' ||
              (isLatest !== true &&
                state.previousValues &&
                Object.keys(state.previousValues)?.length > 0)
            }
            showTime={true}
            disabledDate={(current) =>
              disabledDate(
                current,
                ruleVersionList?.length > 1 ? currentRuleDate : ruleGroupDate
              )
            }
            disabledTime={(current) =>
              disabledTime(
                current,
                ruleVersionList?.length > 1 ? currentRuleDate : ruleGroupDate
              )
            }
            rules={{
              validator: (_, value) => {
                const [start, end] = value;
                const startDate =
                  ruleVersionList?.length > 1
                    ? currentRuleDate[0]
                    : ruleGroupDate[0];
                const endDate =
                  ruleVersionList?.length > 1
                    ? currentRuleDate[1]
                    : ruleGroupDate[1];
                if (start < startDate) {
                  return Promise.reject(
                    new Error(t('rules.messages.ruleStartDate'))
                  );
                }
                if (end > endDate) {
                  return Promise.reject(
                    new Error(t('rules.messages.ruleEndDate'))
                  );
                }
                return Promise.resolve();
              },
            }}
          />
        </Col>
        {ruleType === 'clawback' ? (
          <Col order={6} xs={24} sm={24} lg={8}>
            <DropdownInput
              label={t('rules.clawbackType')}
              name="clawback_type"
              required={true}
              optionList={clawbackTypeOptions}
              message={t('rules.messages.clawbackType')}
              placeholder={t('rules.messages.clawbackType')}
              disabled={type === 'Show' || disabled}
            />
          </Col>
        ) : null}

        <Col order={3} xs={24} sm={24} lg={8}>
          <label className="floating-label">{t('common.status')}</label>
          <SegmentInput
            className="mb-0"
            name="rule_is_active"
            optionList={activeStatus}
            disabled={type === 'Show' || disabled}
          />
        </Col>
        {type?.toLowerCase() !== 'show' ? (
          <Col order={2} xs={24} sm={24} lg={8}>
            <DropdownInput
              label={t('rules.ruleGroups')}
              name="rule_group"
              required={true}
              optionList={getRuleGroupList()}
              message={t('rules.messages.ruleGroup')}
              placeholder={t('rules.messages.ruleGroup')}
              onChange={state.onSelectRuleGroup}
              disabled={type === 'Show'}
            />
          </Col>
        ) : null}
        {ruleType === 'capping' ||
          ruleType === 'booster_contest' ||
          ruleType === 'penalty' ? (
          <Col order={5} xs={24} sm={24} lg={8}>
            <DropdownInput
              label={t('rules.adjustmentLevel')}
              name="adjustment_level"
              required={true}
              optionList={adjustmentLevel}
              message={t('rules.messages.adjustmentLevel')}
              placeholder={t('rules.messages.adjustmentLevel')}
              disabled={disabled || type === 'Show'}
              onChange={handleAdjustmentLevelChange}
            />
          </Col>
        ) : null}

        <Col order={7} xs={24} sm={24} lg={24}>
          <Row>
            <Col xs={24} sm={24} lg={16}>
              <RuleDescription disabled={disabled || type === 'Show'} />
            </Col>
          </Row>
        </Col>
        {adjustmentLevelVal === 'group' ? (
          <Col order={6} xs={24} sm={24} lg={8}>
            <label className="floating-label">{t('rules.configStatus')}</label>
            <SegmentInput
              className="mb-0"
              name="rule_group_configuration_status"
              optionList={ConfigurationStatus}
              disabled={type === 'Show' || disabled}
              onChange={handleConfigStatusChange}
            />
          </Col>
        ) : null}
        <Form.Item name="rule_visibility" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="rule_type" hidden>
          <input type="hidden" />
        </Form.Item>
      </Row>
    </div>
  );
};

export default ruleDetailsItem;
